/**
 * OCTOPUS - EngineNotificationBadges (Phase 16, D-01/D-02)
 *
 * Badges de aviso do painel do motor (PGN 127489/127493) dentro do card do motor
 * no "Ao vivo". Consome `group.engine_notifications` do /live-view (Plano 03):
 * alertas engine_notification ABERTOS já atribuídos por identidade física ao motor.
 *
 * Contrato:
 * - Só ACENDE para avisos ativos; sem aviso ativo → zero DOM (D-02, metáfora do
 *   painel físico: luz apagada não precisa de rótulo). Nunca "Tudo normal".
 * - Paleta de severidade importada de @/lib/alertSeverity (fonte única, D-01) —
 *   idêntica à Central de Alertas.
 * - Severidade e rótulo vêm CURADOS do backend (INV-UI-TRAIL-001); o cliente nunca
 *   deriva severidade do state cru nem renderiza o enum SignalK cru — sempre
 *   metadata.label (regra "UI-REVIEW achado 5").
 * - Detalhe (desdobrável) reusa o formatador PT-BR via useTimezone; nunca
 *   new Date().toLocaleString() manual.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { severityConfig } from '@/lib/alertSeverity';
import { useTimezone } from '@/contexts/TimezoneContext';
import { cn } from '@/lib/utils';

// Pill de severidade — icon + rótulo curado. animate-pulse só no ícone de critical
// (acento de motion do estado urgente, espelha WorkHoldBadge.js).
const SeverityPill = ({ alert }) => {
  const sev = severityConfig[alert.severity] || severityConfig.medium;
  const Icon = sev.icon;
  // Sempre o rótulo curado (metadata.label); jamais o enum cru do warning. Fallback
  // ao rótulo de severidade (também curado) se o label vier ausente.
  const label = alert?.metadata?.label || sev.label;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        sev.bg,
        sev.color,
        sev.border
      )}
    >
      <Icon className={cn('h-3.5 w-3.5 shrink-0', alert.severity === 'critical' && 'animate-pulse')} />
      <span className="truncate">{label}</span>
    </span>
  );
};

const EngineNotificationBadges = ({ alerts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { formatDateTime } = useTimezone();

  // Zero DOM sem aviso ativo (D-02) — nada de card, placeholder ou ícone fantasma.
  if (!alerts?.length) return null;

  const count = alerts.length;
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <div className="px-6 pb-3" data-testid="engine-notification-badges">
      {/* Colapsado: linha de pills sempre visível + trigger do desdobrável */}
      <div className="flex flex-wrap items-center gap-2">
        {alerts.map((alert, idx) => (
          <SeverityPill key={alert.id || idx} alert={alert} />
        ))}
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          aria-expanded={isExpanded}
        >
          <Chevron className="h-4 w-4" />
          {count} {count === 1 ? 'aviso ativo' : 'avisos ativos'}
        </button>
      </div>

      {/* Expandido: uma linha por aviso — rótulo curado, severidade, "Desde {...}"
          e a message crua como texto secundário muted (nunca como rótulo primário). */}
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {alerts.map((alert, idx) => (
            <div key={alert.id || `d-${idx}`} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <SeverityPill alert={alert} />
                <span className="text-xs text-muted-foreground">
                  Desde {formatDateTime(alert.triggered_at, { omitSeconds: true })}
                </span>
              </div>
              {alert.message && (
                <p className="text-xs text-muted-foreground">{alert.message}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EngineNotificationBadges;
